import { useCallback } from 'react';
import { isEmpty } from 'lodash';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { createAlertConfig, SUCCESS } from '../../../../../stores/AlertsStore';
import useUiToasts from '../../../../../stores/ToastStore';
import { useFormSaveService, useFormSubmitWrapper } from '../../../../../services/FormSaveService';
import { useGenericError } from '../../../../../services/FormErrorService';
import { useNavigate } from '../../../../../utilities/PgmUtilities';
import { deepMergeForSave } from '../TestDetailMergeUtils';
import { buildCopyNavigationPath } from '../../DuplicateTestUtils';

const successToastConfig = createAlertConfig(SUCCESS, i18n._(t`Saved Successfully`));

/**
 * Custom save-action handlers for TestDetailPanel.
 *
 * - handleSaveAndNewFn: uses submitWrapper with '../new' as goToUrl (no response needed)
 * - handleSaveAndCopyFn: saves directly, then navigates to ../new?dupOf={savedId}.
 *   CANNOT use submitWrapper because submitWrapper discards the response before navigation.
 *
 * NOTE on service-test save responses:
 * FormSaveService detects `/ngp/v2/service-tests/` and uses PATCH followed by a
 * cache-refresh GET.  The cache-refresh response replaces the PATCH response body,
 * so `response._id` is unreliable for updates.  For existing tests we therefore
 * use the already-known `formData._id`.  For creates, POST returns the new object
 * directly (no cache chain), so `response._id` is valid.
 *
 * @param {Object} params
 * @param {string} params.baseUrl - API base URL, e.g. '/ngp/v2/service-tests/'
 * @param {Object} params.formData - Current form data from react-hook-form context
 * @param {Function} [params.submitTransform] - Transform applied on both create and update paths
 * @param {Function} [params.createSubmitTransform] - Transform applied on create path only
 */
export function useTestDetailSaveActions({
    baseUrl,
    formData,
    submitTransform,
    createSubmitTransform,
    setError,
    currentTypeName,
}) {
    const formSaveService = useFormSaveService();
    const { submitWrapper } = useFormSubmitWrapper();
    const navigate = useNavigate();
    const [, uiToastActions] = useUiToasts();
    const handleGenericError = useGenericError();

    const isCreateAction = isEmpty(formData?._id);

    // Replicate the same transform chain as UseInfrastructureFormSubmit:
    const applyTransforms = useCallback(
        (submitData) => {
            const merged = isCreateAction ? submitData : deepMergeForSave(formData, submitData);
            const afterSubmitTransform = submitTransform ? submitTransform(merged) : merged;
            return isCreateAction && createSubmitTransform
                ? createSubmitTransform(afterSubmitTransform)
                : afterSubmitTransform;
        },
        [formData, isCreateAction, submitTransform, createSubmitTransform]
    );

    // Save-and-New: same submit wrapper mechanics, just a different goToUrl.
    // Caller: handleSubmit(handleSaveAndNewFn)()
    const underlyingSaveFn = useCallback(
        (submitData) => {
            const dataToSave = applyTransforms(submitData);
            return formSaveService.save({
                url: baseUrl,
                recordId: isCreateAction ? undefined : formData._id,
                dataToSave: isCreateAction ? dataToSave : { ...dataToSave, _id: formData._id },
                setError,
            });
        },
        [applyTransforms, formSaveService, baseUrl, isCreateAction, formData, setError]
    );

    const saveAndNewUrl = currentTypeName ? `../new?name=${encodeURIComponent(currentTypeName)}` : '../new';
    const handleSaveAndNewFn = submitWrapper(underlyingSaveFn, saveAndNewUrl);

    // Save-and-Copy: save then navigate with the saved test's ID.
    const handleSaveAndCopyFn = useCallback(
        async (submitData) => {
            try {
                const dataToSave = applyTransforms(submitData);
                const response = await formSaveService.save({
                    url: baseUrl,
                    recordId: isCreateAction ? undefined : formData._id,
                    dataToSave: isCreateAction ? dataToSave : { ...dataToSave, _id: formData._id },
                    setError,
                });

                // For existing tests, use formData._id (PATCH + cache-refresh replaces
                // the response body). For new tests, POST returns the created object.
                const savedId = isCreateAction ? response?._id : formData._id;
                if (savedId) {
                    uiToastActions.createToast({ ...successToastConfig, createOnNavigate: true });
                    navigate(buildCopyNavigationPath(savedId, { fromDetail: true }));
                }
            } catch (error) {
                handleGenericError(error);
            }
        },
        [
            applyTransforms,
            formSaveService,
            baseUrl,
            isCreateAction,
            formData,
            setError,
            uiToastActions,
            navigate,
            handleGenericError,
        ]
    );

    return { handleSaveAndNewFn, handleSaveAndCopyFn };
}
